import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY = {
  name: '', dob: '', gender: '', phone: '', email: '',
  address: '', bloodGroup: '', emergencyContact: '',
};

function printInvoice(inv, info, patientName) {
  const fmtR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const subtotal = (Number(inv.consult_fee) + Number(inv.medicine_charges) + Number(inv.lab_charges) + Number(inv.other_charges) - Number(inv.discount)) || 0;
  const h = info || {};
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Invoice ${inv.invoice_no}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 32px; max-width: 700px; margin: auto; }
  .hospital-name { font-size: 22px; font-weight: 800; color: #1b6ca8; }
  .hospital-sub { font-size: 12px; color: #555; margin-top: 3px; }
  .divider { border: none; border-top: 2px solid #1b6ca8; margin: 14px 0; }
  .divider-thin { border: none; border-top: 1px solid #ddd; margin: 10px 0; }
  .invoice-title { font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 2px; margin: 10px 0; color: #1b6ca8; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin: 10px 0; }
  .meta-grid .lbl { font-size: 11px; color: #777; }
  .meta-grid .val { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th { background: #e8f4fd; color: #1b6ca8; font-size: 12px; font-weight: 700; padding: 7px 10px; text-align: left; }
  td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; }
  .amount { text-align: right; }
  .total-row { font-weight: 800; font-size: 15px; color: #27ae60; background: #e6f4ea; }
  .total-row td { padding: 9px 10px; }
  .paid-badge { display: inline-block; padding: 4px 12px; border-radius: 6px; font-weight: 700; font-size: 12px; }
  .paid { background: #e6f4ea; color: #27ae60; }
  .pending { background: #fce8e6; color: #c5221f; }
  .footer-note { font-size: 11px; color: #999; text-align: center; margin-top: 20px; }
  @media print { body { padding: 0; } button { display: none; } }
</style></head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start">
  <div>
    <div class="hospital-name">${h.hospital_name || 'Hospital'}</div>
    <div class="hospital-sub">${[h.address, h.phone, h.email].filter(Boolean).join(' | ')}</div>
    ${h.license_no ? `<div class="hospital-sub">License: ${h.license_no}${h.gst_no ? ' | GST: ' + h.gst_no : ''}</div>` : ''}
  </div>
  <div style="text-align:right">
    <div class="paid-badge ${inv.status === 'paid' ? 'paid' : 'pending'}">${(inv.status || '').toUpperCase()}</div>
  </div>
</div>
${h.bill_header ? `<div style="font-size:12px;color:#555;margin-top:6px">${h.bill_header}</div>` : ''}
<hr class="divider">
<div class="invoice-title">TAX INVOICE</div>
<div class="meta-grid">
  <div><div class="lbl">Invoice No</div><div class="val">${inv.invoice_no || '—'}</div></div>
  <div><div class="lbl">Invoice Date</div><div class="val">${inv.invoice_date || '—'}</div></div>
  <div><div class="lbl">Patient</div><div class="val">${inv.patient_name || patientName || '—'}</div></div>
  <div><div class="lbl">Doctor</div><div class="val">${inv.doctor_name ? 'Dr. ' + inv.doctor_name : '—'}</div></div>
</div>
<hr class="divider-thin">
<table>
  <thead><tr><th>Particulars</th><th class="amount">Amount</th></tr></thead>
  <tbody>
    ${Number(inv.consult_fee) > 0 ? `<tr><td>Consultation Fee</td><td class="amount">${fmtR(inv.consult_fee)}</td></tr>` : ''}
    ${Number(inv.medicine_charges) > 0 ? `<tr><td>Medicine Charges</td><td class="amount">${fmtR(inv.medicine_charges)}</td></tr>` : ''}
    ${Number(inv.lab_charges) > 0 ? `<tr><td>Lab Charges</td><td class="amount">${fmtR(inv.lab_charges)}</td></tr>` : ''}
    ${Number(inv.other_charges) > 0 ? `<tr><td>Other Charges</td><td class="amount">${fmtR(inv.other_charges)}</td></tr>` : ''}
    ${Number(inv.discount) > 0 ? `<tr><td>Discount</td><td class="amount" style="color:#c0392b">− ${fmtR(inv.discount)}</td></tr>` : ''}
    <tr style="background:#fafafa"><td style="font-weight:600">Subtotal</td><td class="amount" style="font-weight:600">${fmtR(subtotal)}</td></tr>
    ${Number(inv.tax_pct) > 0 ? `<tr><td>Tax (${inv.tax_pct}%)</td><td class="amount">${fmtR(inv.tax_amount)}</td></tr>` : ''}
  </tbody>
  <tfoot>
    <tr class="total-row"><td>TOTAL</td><td class="amount">${fmtR(inv.total)}</td></tr>
  </tfoot>
</table>
${inv.status === 'paid' ? `<div style="margin:10px 0;font-size:13px"><strong>Payment:</strong> ${fmtR(inv.paid_amount)} via ${(inv.payment_mode || '').toUpperCase()}</div>` : ''}
${inv.notes ? `<div style="margin-top:10px;font-size:12px;color:#555"><strong>Notes:</strong> ${inv.notes}</div>` : ''}
${h.bill_footer ? `<div style="font-size:12px;color:#555;margin-top:10px;border-top:1px solid #eee;padding-top:10px">${h.bill_footer}</div>` : ''}
<div class="footer-note">This is a computer-generated invoice and does not require a signature.</div>
<div style="text-align:center;margin-top:10px">
  <button onclick="window.print()" style="padding:8px 24px;background:#1b6ca8;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer">🖨 Print</button>
</div>
</body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalPatients({ role }) {
  const [patients, setPatients]   = useState([]);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [detail, setDetail]       = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [hospitalInfo, setHospitalInfo] = useState({});

  const load = useCallback(() => {
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { hospitalApi.getInfo().then(d => { if (d) setHospitalInfo(d); }); }, []);

  const loadDetail = useCallback((id) => {
    setDetailLoading(true);
    hospitalApi.getPatient(id).then(d => {
      if (d) setDetail(d);
      setDetailLoading(false);
    }).catch(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    if (selected) loadDetail(selected);
  }, [selected, loadDetail]);

  const filtered = patients.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) || p.patient_code?.toLowerCase().includes(search.toLowerCase())
  );

  const resetForm = () => { setForm(EMPTY); setEditId(null); setErr(''); };

  const startEdit = (p) => {
    setForm({
      name: p.name || '', dob: p.dob || '', gender: p.gender || '',
      phone: p.phone || '', email: p.email || '', address: p.address || '',
      bloodGroup: p.blood_group || '', emergencyContact: p.emergency_contact || '',
    });
    setEditId(p.id);
    setShowForm(true);
    setErr('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await hospitalApi.updatePatient(editId, form);
      else await hospitalApi.createPatient(form);
      load();
      setShowForm(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this patient? This cannot be undone.')) return;
    await hospitalApi.deletePatient(id);
    if (selected === id) { setSelected(null); setDetail(null); }
    load();
  };

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 130px)' }}>
      {/* Left: patient list */}
      <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Search + Add */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-control" placeholder="Search by name, phone, code…"
            value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
          <button className="btn btn-primary" style={{ whiteSpace: 'nowrap', fontSize: 13 }}
            onClick={() => { resetForm(); setShowForm(true); }}>
            + Add Patient
          </button>
        </div>

        {/* Patient table */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff', borderRadius: 10,
          boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: LIGHT, position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>Name</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>Gender</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>Phone</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id}
                  onClick={() => setSelected(p.id)}
                  style={{
                    borderTop: '1px solid #f1f3f4',
                    background: selected === p.id ? LIGHT : i % 2 === 0 ? '#fff' : '#fafbfc',
                    cursor: 'pointer',
                  }}>
                  <td style={{ padding: '8px 12px', fontSize: 11, color: '#888' }}>{p.patient_code}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: DARK }}>{p.name}</td>
                  <td style={{ padding: '8px 12px', color: '#5f6368' }}>{p.gender || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{p.phone || '—'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#9aa0a6' }}>No patients found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: Add/Edit form or Patient Detail */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {showForm && (
          <div className="card">
            <div className="card-header">
              <div className="card-title">{editId ? 'Edit Patient' : 'Add New Patient'}</div>
              <button style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#5f6368' }}
                onClick={() => { setShowForm(false); resetForm(); }}>×</button>
            </div>
            <div className="card-body">
              {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                <F label="Full Name *">
                  <input {...inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </F>
                <F label="Date of Birth">
                  <input {...inp} type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} />
                </F>
                <F label="Gender">
                  <select {...inp} value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </F>
                <F label="Phone">
                  <input {...inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </F>
                <F label="Email">
                  <input {...inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </F>
                <F label="Blood Group">
                  <select {...inp} value={form.bloodGroup} onChange={e => setForm(f => ({ ...f, bloodGroup: e.target.value }))}>
                    <option value="">Select</option>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </F>
                <F label="Emergency Contact">
                  <input {...inp} value={form.emergencyContact} onChange={e => setForm(f => ({ ...f, emergencyContact: e.target.value }))} />
                </F>
                <F label="Address">
                  <textarea {...inp} rows={2} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={{ resize: 'vertical' }} />
                </F>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Patient'}
                </button>
                <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                  background: '#fff', cursor: 'pointer', fontSize: 13 }}
                  onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {!showForm && selected && (
          detailLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>Loading patient details…</div>
          ) : detail ? (
            <PatientDetail detail={detail} onEdit={startEdit} onDelete={handleDelete} role={role} fmtRs={fmtRs} C={C} DARK={DARK} LIGHT={LIGHT} hospitalInfo={hospitalInfo} />
          ) : null
        )}

        {!showForm && !selected && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '60%', color: '#9aa0a6', fontSize: 15 }}>
            Select a patient to view details
          </div>
        )}
      </div>
    </div>
  );
}

function AiInsights({ patientId, C, DARK, LIGHT }) {
  const [loading, setLoading]   = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError]       = useState('');

  // Auto-load cached insights whenever the patient changes
  useEffect(() => {
    setInsights(null);
    setError('');
    let cancelled = false;
    hospitalApi.getCachedAiInsights(patientId).then(data => {
      if (cancelled) return;
      if (data && !data.error && data.cached !== false) setInsights(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [patientId]);

  async function fetchInsights() {
    setLoading(true);
    setError('');
    try {
      const data = await hospitalApi.getAiInsights(patientId);
      if (data && data.error) { setError(data.error); setInsights(null); }
      else setInsights(data);
    } catch (e) {
      setError(e.message || 'AI analysis failed');
    } finally {
      setLoading(false);
    }
  }

  const Section = ({ label, color, bg, items }) => {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 6,
          textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((item, i) => (
            <div key={i} style={{ background: bg, border: `1px solid ${color}22`,
              borderLeft: `3px solid ${color}`, borderRadius: 6,
              padding: '7px 12px', fontSize: 13, color: DARK, lineHeight: 1.5 }}>
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const cacheLabel = insights
    ? (insights.fromCache
        ? `Cached · ${insights.cachedAt ? insights.cachedAt.substring(0, 10) : (insights.generatedAt || '')}`
        : `Freshly analysed · ${insights.generatedAt || ''}`)
    : null;

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="card-title">🤖 AI Insights</div>
          {cacheLabel && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
              background: insights.fromCache ? '#e6f4ea' : '#e8f4fd',
              color: insights.fromCache ? '#27ae60' : '#1b6ca8', letterSpacing: '0.03em' }}>
              {cacheLabel}
            </span>
          )}
        </div>
        <button
          onClick={fetchInsights}
          disabled={loading}
          style={{ fontSize: 12, padding: '4px 14px',
            background: loading ? '#f1f3f4' : C, color: loading ? '#888' : '#fff',
            border: 'none', borderRadius: 6, cursor: loading ? 'default' : 'pointer',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          {loading ? '⏳ Analysing...' : insights ? '↻ Re-analyse' : '✨ Analyse Patient'}
        </button>
      </div>
      <div className="card-body">
        {!insights && !loading && !error && (
          <div style={{ textAlign: 'center', color: '#9aa0a6', fontSize: 13, padding: '12px 0' }}>
            Click <strong>Analyse Patient</strong> to get AI-powered clinical observations,
            treatment recommendations, drug-interaction warnings and lab flags.
          </div>
        )}
        {error && (
          <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
            padding: '10px 14px', fontSize: 13 }}>⚠ {error}</div>
        )}
        {insights && (
          <>
            <Section label="Clinical Observations"   color="#1b6ca8" bg="#e8f4fd" items={insights.observations} />
            <Section label="Recommendations"         color="#27ae60" bg="#e6f4ea" items={insights.recommendations} />
            <Section label="Drug Interactions"       color="#e67e22" bg="#fef9e7" items={insights.interactions} />
            <Section label="Flags / Watch Points"    color="#c0392b" bg="#fce8e6" items={insights.flags} />
            <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 10, textAlign: 'right' }}>
              AI advisory · Doctor's clinical judgment takes precedence
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PatientDetail({ detail, onEdit, onDelete, role, fmtRs, C, DARK, LIGHT, hospitalInfo }) {
  const [expandTreatment, setExpandTreatment] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Patient Header */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">👥 {detail.name}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ fontSize: 12, padding: '4px 10px', background: LIGHT,
              color: DARK, border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              onClick={() => onEdit(detail)}>Edit</button>
            {role === 'admin' && (
              <button style={{ fontSize: 12, padding: '4px 10px', background: '#fce8e6',
                color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => onDelete(detail.id)}>Delete</button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {[
              { label: 'Patient Code', value: detail.patient_code },
              { label: 'Gender', value: detail.gender },
              { label: 'Date of Birth', value: detail.dob },
              { label: 'Phone', value: detail.phone },
              { label: 'Email', value: detail.email },
              { label: 'Blood Group', value: detail.blood_group },
              { label: 'Emergency Contact', value: detail.emergency_contact },
            ].filter(f => f.value).map((f, i) => (
              <div key={i} style={{ background: LIGHT, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: '#5f6368', marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: DARK }}>{f.value}</div>
              </div>
            ))}
            {detail.address && (
              <div style={{ background: LIGHT, borderRadius: 8, padding: '10px 12px', gridColumn: '1/-1' }}>
                <div style={{ fontSize: 11, color: '#5f6368', marginBottom: 2 }}>Address</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: DARK }}>{detail.address}</div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 12, color: '#5f6368' }}>
              Appointments: <strong style={{ color: DARK }}>{detail.appointmentsCount || 0}</strong>
            </span>
            {detail.lastVisit && (
              <span style={{ fontSize: 12, color: '#5f6368' }}>
                Last Visit: <strong style={{ color: DARK }}>{detail.lastVisit}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Recent Appointments */}
      <SectionCard title="📅 Recent Appointments" items={detail.recentAppointments || []}
        headers={['Date', 'Doctor', 'Type', 'Status']}
        renderRow={a => [a.appt_date, a.doctor_name || '—', a.type || '—', a.status]}
        LIGHT={LIGHT} DARK={DARK} C={C} />

      {/* Recent Treatments */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">💊 Recent Treatments</div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {(detail.recentTreatments || []).length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#9aa0a6', fontSize: 13 }}>No treatments recorded</div>
          ) : (detail.recentTreatments || []).map((t, i) => (
            <div key={t.id} style={{ borderTop: i > 0 ? '1px solid #f1f3f4' : 'none' }}>
              <div
                style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  background: expandTreatment === t.id ? LIGHT : 'transparent', alignItems: 'center' }}
                onClick={() => setExpandTreatment(expandTreatment === t.id ? null : t.id)}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: DARK }}>{t.diagnosis || 'No diagnosis'}</div>
                  <div style={{ fontSize: 11, color: '#5f6368' }}>{t.visit_date} · Dr. {t.doctor_name || '—'}</div>
                </div>
                <span style={{ fontSize: 12, color: C }}>
                  {expandTreatment === t.id ? '▲' : '▼'} {(t.prescriptions || []).length} Rx
                </span>
              </div>
              {expandTreatment === t.id && (
                <div style={{ padding: '8px 14px 12px', background: '#fafbfc', borderTop: '1px solid #f1f3f4' }}>
                  {t.treatment_notes && (
                    <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 8 }}>Notes: {t.treatment_notes}</div>
                  )}
                  {(t.prescriptions || []).length > 0 ? (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: DARK, marginBottom: 6 }}>Prescriptions:</div>
                      {t.prescriptions.map(rx => (
                        <div key={rx.id} style={{ fontSize: 12, padding: '4px 8px', background: '#fff',
                          borderRadius: 6, marginBottom: 4, border: '1px solid #e8eaed' }}>
                          💊 <strong>{rx.medicine_name}</strong> — {rx.dosage} · {rx.frequency} · {rx.duration}
                          {rx.instructions && <span style={{ color: '#888' }}> ({rx.instructions})</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9aa0a6' }}>No prescriptions</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lab Reports */}
      <SectionCard title="🧪 Lab Reports" items={detail.recentLabReports || []}
        headers={['Test', 'Date', 'Doctor', 'Status']}
        renderRow={r => [r.test_name, r.test_date, r.doctor_name || '—', r.status]}
        LIGHT={LIGHT} DARK={DARK} C={C} />

      {/* AI Insights */}
      <AiInsights patientId={detail.id} C={C} DARK={DARK} LIGHT={LIGHT} />

      {/* Invoices */}
      <div className="card">
        <div className="card-header"><div className="card-title">💰 Invoice History</div></div>
        <div className="card-body" style={{ padding: 0 }}>
          {(detail.invoices || []).length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#9aa0a6', fontSize: 13 }}>No invoices</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Invoice No', 'Date', 'Doctor', 'Total', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.invoices.map((inv, i) => (
                  <tr key={inv.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '8px 12px', fontSize: 11, color: '#888' }}>{inv.invoice_no}</td>
                    <td style={{ padding: '8px 12px' }}>{inv.invoice_date}</td>
                    <td style={{ padding: '8px 12px', color: '#5f6368' }}>{inv.doctor_name || '—'}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#27ae60' }}>{fmtRs(inv.total)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        background: inv.status === 'paid' ? '#e6f4ea' : '#fce8e6',
                        color: inv.status === 'paid' ? '#27ae60' : '#c5221f',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                      }}>{inv.status}</span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button onClick={() => printInvoice({ ...inv, patient_name: detail.name }, hospitalInfo, detail.name)}
                        style={{ fontSize: 11, padding: '3px 10px', background: LIGHT, color: DARK,
                          border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
                        🖨 Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, items, headers, renderRow, LIGHT, DARK, C }) {
  return (
    <div className="card">
      <div className="card-header"><div className="card-title">{title}</div></div>
      <div className="card-body" style={{ padding: 0 }}>
        {items.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9aa0a6', fontSize: 13 }}>No records</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: LIGHT }}>
                {headers.map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  {renderRow(item).map((val, j) => (
                    <td key={j} style={{ padding: '8px 12px', color: j === 0 ? DARK : '#5f6368', fontWeight: j === 0 ? 600 : 400 }}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
